import React, { useEffect, useMemo, useRef, useState } from 'react';
import { notificationsAPI } from '../../services/api';

const useSwipe = (onSwipe) => {
	const startX = useRef(null);
	const threshold = 50;
	const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
	const onTouchMove = (e, id) => {
		if (startX.current === null) return;
		const dx = e.touches[0].clientX - startX.current;
		if (dx <= -threshold) {
			onSwipe(id);
			startX.current = null;
		}
	};
	const onTouchEnd = () => { startX.current = null; };
	return { onTouchStart, onTouchMove, onTouchEnd };
};

export default function NotificationDropdown({ isOpen, onClose }) {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [unreadCount, setUnreadCount] = useState(0);

	const load = async () => {
		try {
			setLoading(true);
			const { data } = await notificationsAPI.list({ limit: 50 });
			setItems(data.notifications || []);
			setUnreadCount((data.notifications || []).filter(n => !n.isRead).length);
		} catch (_) {} finally { setLoading(false); }
	};

	useEffect(() => { if (isOpen) load(); }, [isOpen]);

	const pollRef = useRef(null);
	useEffect(() => {
		pollRef.current = setInterval(load, 60_000);
		return () => clearInterval(pollRef.current);
	}, []);

	const handleDelete = async (id) => {
		try { await notificationsAPI.delete(id); setItems(prev => prev.filter(n => n.id !== id)); } catch (_) {}
	};
	const handleRead = async (id) => {
		try { await notificationsAPI.markRead(id); setItems(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n)); } catch (_) {}
	};
	const handleClearRead = async () => {
		try { await notificationsAPI.deleteRead(); await load(); } catch (_) {}
	};

	const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe(handleDelete);

	if (!isOpen) return null;
	return (
		<div className="absolute right-0 mt-2 w-80 bg-white shadow-lg rounded-md ring-1 ring-black ring-opacity-5 z-50">
			<div className="p-3 flex items-center justify-between border-b">
				<div className="font-semibold">Notifications</div>
				<div className="text-sm text-gray-500">{unreadCount} unread</div>
			</div>
			<div className="max-h-80 overflow-auto">
				{loading && <div className="p-3 text-sm text-gray-500">Loading…</div>}
				{!loading && items.length === 0 && <div className="p-3 text-sm text-gray-500">No notifications</div>}
				{items.map(n => (
					<div key={n.id}
						className={`px-3 py-2 border-b flex items-start gap-2 ${n.isRead ? 'bg-white' : 'bg-green-50'}`}
						onTouchStart={onTouchStart}
						onTouchMove={(e) => onTouchMove(e, n.id)}
						onTouchEnd={onTouchEnd}
					>
						<div className="flex-1">
							<div className="font-medium text-sm">{n.title}</div>
							<div className="text-xs text-gray-600">{n.body}</div>
							<div className="text-[10px] text-gray-400 mt-1">{new Date(n.createdAt).toLocaleString()}</div>
						</div>
						<div className="flex gap-2">
							<button className="text-xs text-gray-500 hover:text-gray-700" onClick={() => handleRead(n.id)}>Mark read</button>
							<button className="text-xs text-red-500 hover:text-red-600" onClick={() => handleDelete(n.id)}>Delete</button>
						</div>
					</div>
				))}
			</div>
			<div className="p-2 flex items-center justify-between">
				<button className="text-xs text-gray-500 hover:text-gray-700" onClick={handleClearRead}>Clear read</button>
				<button className="text-xs text-gray-500 hover:text-gray-700" onClick={onClose}>Close</button>
			</div>
		</div>
	);
}


